<template>
  <div data-testid="cart_product">
    <RxBasketCardDefault
      v-if="!confirmDeleteOpen"
      v-bind="$props"
      :basket-item="basketItem"
      :product="product"
      :variant="variant"
      :quantity="quantity"
      :item-group="itemGroup as ItemGroupMainItem"
      :configurator-items="configuratorItems"
      @click:delete="onPressDelete"
      @click:edit-configuration-step="editConfiguration"
      @click:edit-prescription-values="editPrescriptionValues"
      @click:change-quantity="
        (quantity: number) => $emit('click:change-quantity', quantity)
      "
    />
    <BasketCardConfirmDelete
      v-else
      :loading="loading"
      @click:confirm="onConfirmDelete"
      @click:cancel="onCancelDelete"
    />
  </div>
</template>
<script setup lang="ts">
import type { Product, Variant } from '@scayle/storefront-nuxt'
import type {
  FimBasketItemCustomDataMain,
  ItemGroup,
} from '~/composables/useFimBasket'
import {
  useRxConfiguratorState,
  type ConfiguratorSerializedMain,
  type ConfiguratorSerializedAddon,
} from '~/composables/useRxConfiguratorState'
import type { RxConfMasterKeys } from '~/rpcMethods/rxTypes'
import useUiState from '~/composables/ui/useUiState'

const props = defineProps({
  basketItem: {
    type: Object as PropType<FimBasketItem>,
    required: true,
  },
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  variant: {
    type: Object as PropType<Variant>,
    required: true,
  },
  quantity: {
    type: Number as PropType<number>,
    default: 1,
  },
  customData: {
    type: Object as PropType<
      FimBasketItemCustomData | FimBasketItemCustomDataMain
    >,
    required: true,
  },
  itemGroup: {
    type: Object as PropType<ItemGroup>,
    required: true,
  },
  isRxItemUnavailable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'click:change-quantity',
  'click:remove-from-basket',
  'click:edit-configuration-step',
  'click:edit-prescription-values',
])

const loading = ref(false)
const confirmDeleteOpen = ref(false)

const onPressDelete = () => {
  confirmDeleteOpen.value = true
}

const onCancelDelete = () => {
  confirmDeleteOpen.value = false
}

const onConfirmDelete = () => {
  loading.value = true
  emit('click:remove-from-basket')
}

const { deserialize } = await useRxConfiguratorState()
const { openRxConfiguratorOverlay } = useUiState()

const configuratorItems = computed(() => {
  const mainItem: ConfiguratorSerializedMain = {
    variantId: props.variant.id,
    quantity: props.quantity,
    itemGroup: props.itemGroup,
    customData: props.customData as ConfiguratorSerializedMainCustomData,
  }
  const configurationItems = props.customData.configurationItems
    ? props.customData.configurationItems?.map(
        (item): ConfiguratorSerializedAddon => {
          return {
            variantId: Number(item.id),
            quantity: props.quantity,
            itemGroup: {
              id: props.itemGroup?.id ?? '0',
              isMainItem: false,
              isRequired: true,
            },
            customData: {
              shopId: props.customData.shopId ?? 0,
              hideSubItem: true,
            },
          }
        },
      )
    : []
  return [mainItem, ...configurationItems]
})

const editPrescriptionValues = async () => {
  // set proper rxConfigurator state
  await deserialize(configuratorItems.value)

  // emit event to basket to open configurator
  emit('click:edit-configuration-step', {
    product: props.product,
    variant: props.variant,
    initialStep: 2,
    hasPrescriptionValues: true,
    editingGroupId: props.itemGroup?.id,
    basketItemPayload: props.basketItem,
  })
}
const editConfiguration = async (stepMasterKey: RxConfMasterKeys) => {
  // set proper rxConfigurator state
  await deserialize(configuratorItems.value)

  // determine configurator step based on masterKey
  const hasPrescriptionValues = Boolean(props.customData.prescriptionValues)
  const initialStep = getConfStepIndexByMasterKey(
    stepMasterKey,
    hasPrescriptionValues,
  )
  // open rxConfigurator on the correct step
  openRxConfiguratorOverlay()

  // emit event to basket to open configurator
  emit('click:edit-configuration-step', {
    product: props.product,
    variant: props.variant,
    initialStep,
    hasPrescriptionValues,
    editingGroupId: props.itemGroup?.id,
    basketItemPayload: props.basketItem,
  })
}
</script>
